
import { defineComponent } from "vue";
import dayjs from "dayjs";

const MIN_DATE = dayjs("2026-01-01").startOf("day");
const MOBILE_MQ = "(max-width: 767.98px)";

const PRESET_OPTIONS = [
  "Today",
  "Last 7 Days",
  "This Month",
  "Last Month",
  "Custom"
] as const;

type PresetOption = (typeof PRESET_OPTIONS)[number];

export default defineComponent({
  name: "SeqDateRangePicker",
  emits: ["change"],
  data() {
    const isMobile =
      typeof window !== "undefined" && window.matchMedia(MOBILE_MQ).matches;
    return {
      presetOptions: PRESET_OPTIONS,
      preset: "This Month" as PresetOption,
      presetMenuOpen: false,
      mobilePanelOpen: false,
      isMobile,
      fromDate: "",
      toDate: "",
      customRange: null as string[] | null,
      minDateStr: MIN_DATE.format("YYYY-MM-DD")
    };
  },
  computed: {
    dateRangeLabel(): string {
      if (!this.fromDate || !this.toDate) {
        return "";
      }
      const start = dayjs(this.fromDate).format("DD MMM YYYY");
      const end = dayjs(this.toDate).format("DD MMM YYYY");
      return `${start} - ${end}`;
    }
  },
  mounted() {
    this.updateIsMobile();
    this.applyPreset("This Month");
    document.addEventListener("click", this.handleDocumentClick);
    window.addEventListener("resize", this.updateIsMobile);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
    window.removeEventListener("resize", this.updateIsMobile);
  },
  methods: {
    updateIsMobile() {
      this.isMobile = window.matchMedia(MOBILE_MQ).matches;
      if (!this.isMobile) {
        this.mobilePanelOpen = false;
      }
    },
    disabledDate(time: Date) {
      return dayjs(time).isBefore(MIN_DATE, "day");
    },
    clampStartDate(date: dayjs.Dayjs) {
      return date.isBefore(MIN_DATE, "day") ? MIN_DATE : date;
    },
    togglePresetMenu() {
      this.presetMenuOpen = !this.presetMenuOpen;
      if (this.presetMenuOpen) {
        this.mobilePanelOpen = false;
      }
    },
    onCalendarClick() {
      if (!this.isMobile) {
        return;
      }
      this.mobilePanelOpen = !this.mobilePanelOpen;
      this.presetMenuOpen = false;
    },
    handleDocumentClick(event: MouseEvent) {
      const rootRef = this.$refs.rootRef as HTMLElement | undefined;
      if (rootRef && !rootRef.contains(event.target as Node)) {
        this.presetMenuOpen = false;
        this.mobilePanelOpen = false;
      }
    },
    selectPreset(option: PresetOption) {
      this.presetMenuOpen = false;
      this.mobilePanelOpen = false;
      if (option === "Custom") {
        this.preset = option;
        if (this.isMobile) {
          this.mobilePanelOpen = true;
        }
        return;
      }
      this.applyPreset(option);
    },
    applyPreset(option: PresetOption) {
      this.preset = option;
      const today = dayjs().startOf("day");
      let start = today;
      let end = today;

      if (option === "Last 7 Days") {
        start = today.subtract(6, "day");
      } else if (option === "This Month") {
        start = today.startOf("month");
      } else if (option === "Last Month") {
        start = today.subtract(1, "month").startOf("month");
        end = today.subtract(1, "month").endOf("month");
      }

      start = this.clampStartDate(start);
      if (end.isBefore(start, "day")) {
        end = start;
      }

      this.fromDate = start.format("YYYY-MM-DD");
      this.toDate = end.format("YYYY-MM-DD");
      this.customRange = [this.fromDate, this.toDate];
      this.emitChange();
    },
    onCustomRangeChange(value: string[] | null) {
      if (!Array.isArray(value) || value.length !== 2) {
        return;
      }
      this.commitRange(value[0], value[1]);
    },
    onMobileFromChange(event: Event) {
      const value = (event.target as HTMLInputElement).value;
      this.fromDate = value;
      if (this.toDate && dayjs(this.toDate).isBefore(dayjs(value), "day")) {
        this.toDate = value;
      }
    },
    onMobileToChange(event: Event) {
      this.toDate = (event.target as HTMLInputElement).value;
    },
    applyMobileDates() {
      if (!this.fromDate || !this.toDate) {
        return;
      }
      this.commitRange(this.fromDate, this.toDate);
      this.mobilePanelOpen = false;
    },
    commitRange(from: string, to: string) {
      let start = dayjs(from);
      let end = dayjs(to);
      start = this.clampStartDate(start);

      if (end.isBefore(start, "day")) {
        end = start;
      }

      this.fromDate = start.format("YYYY-MM-DD");
      this.toDate = end.format("YYYY-MM-DD");
      this.customRange = [this.fromDate, this.toDate];
      this.preset = "Custom";
      this.emitChange();
    },
    emitChange() {
      this.$emit("change", {
        preset: this.preset,
        fromDate: this.fromDate,
        toDate: this.toDate
      });
    }
  }
});
